import { pubsub } from '@/javascript/service/pubsub';

class LogLevel {
  static ERROR = 'error';
  static WARN = 'warn';
  static INFO = 'info';
  static DEBUG = 'debug';
  static TRACE = 'trace';
  static LOG = 'log';
}
class Logger {
  /**
   * @type {boolean}
   */
  enabled;

  // Properties whose names match this regex will be filtered out when using dir/table
  static ignore = /^(aria)/i;

  constructor() {
    this.enabled = true;
  }

  /**
   * Shallowly filter out properties from objects/arrays whose keys match Logger.ignore.
   * For non-objects, the value is returned unchanged.
   * @param {*} value
   * @returns {*}
   */
  static filter(value) {
    try {
      if (Array.isArray(value)) {
        return value.map(item => (item && typeof item === 'object' ? Logger.filter(item) : item));
      }
      if (value && typeof value === 'object') {
        const output = {};
        for (const key of Object.keys(value)) {
          if (!Logger.ignore.test(key)) {
            const v = value[key];
            output[key] = v && (Array.isArray(v) || typeof v === 'object') ? Logger.filter(v) : v;
          }
        }
        return output;
      }
      return value;
    } catch {
      return value;
    }
  }

  /**
   * Remove properties from an object that have null values.
   * @param {*} object
   * @returns {*} the stripped object
   */
  static stripNull = object => {
    const properties = Object.keys(object);
    const keysToRemove = properties.map(it => null === object[it]);
    keysToRemove.forEach(it => delete object[it]);
    return object;
  };

  message(logLevel, message, ...anything) {
    if (this.enabled) {
      const func = console[logLevel];
      if ('function' === typeof func) {
        func(message, ...anything);
        pubsub.pub(Events.message, message);
      }
    }
  }

  /**
   * Log a message to the console.
   * @param {string} message
   * @param {*} [anything] - Optional parameter of any type
   * @returns {Logger}
   */
  log(message, ...anything) {
    this.enabled && console.log(message, ...anything);
    return this;
  }

  /**
   * Log a warning to the console.
   * @param {string} message
   * @param {*} [anything] - Optional parameter of any type
   * @returns {Logger}
   */
  warn(message, ...anything) {
    this.enabled && console.warn(message, ...anything);
    return this;
  }

  /**
   * Log an error to the console.
   * @param {string} message
   * @param {*} [anything] - Optional parameter of any type
   * @returns {Logger}
   */
  error(message, ...anything) {
    this.enabled && console.error(message, ...anything);
    return this;
  }

  /**
   * Log a debug message to the console.
   * @param {string} message
   * @param {*} [anything] - Optional parameter of any type
   * @returns {Logger}
   */
  debug(message, ...anything) {
    this.enabled && console.debug(message, ...anything);
    return this;
  }

  /**
   * Log a table to the console.
   * @param {*} message - Data to display in table format
   * @param {boolean} filterProperties
   * @param {boolean} stripNullProperties
   * @returns {Logger}
   */
  table(message, filterProperties = true, stripNullProperties = true) {
    if (!this.enabled) return this;
    if (filterProperties) {
      message = Logger.filter(message);
    }
    if (stripNullProperties) {
      message = Logger.stripNull(message);
    }
    console.table(message);
  }

  /**
   * Dissect an object in the console.
   * @param {*} message - Object to inspect
   * @param {boolean} filterProperties
   * @param {boolean} stripNullProperties
   * @returns {Logger}
   */
  dir(message, filterProperties = true, stripNullProperties = true) {
    if (!this.enabled) return this;
    if (filterProperties) {
      message = Logger.filter(message);
    }
    if (stripNullProperties) {
      message = Logger.stripNull(message);
    }
    console.dir(message);
    return this;
  }

  /**
   * Group a set of console messages together.
   * @param {string} name - Group name
   * @returns {Logger}
   */
  group(name) {
    this.enabled && console.group(name);
    return this;
  }

  /**
   * End a group of console messages.
   * @returns {Logger}
   */
  groupEnd() {
    this.enabled && console.groupEnd();
    return this;
  }

  /**
   * Log a stack trace to the console.
   * @param {string} message
   * @param {*} [anything] - Optional parameter of any type
   */
  trace(message, ...anything) {
    this.enabled && console.trace(message, ...anything);
  }

  /**
   * Log an info message to the console.
   * @param {string} message
   * @param {*} [anything] - Optional parameter of any type
   */
  info(message, ...anything) {
    this.enabled && console.info(message, ...anything);
  }
}

const logger = new Logger();
export default logger;
